import { useState, useCallback, useRef } from 'react';
import { Box, TextField, IconButton, Button, Typography } from '@mui/material';
import {
  AddCircleOutline as AddIcon,
  RemoveCircleOutline as RemoveIcon,
} from '@mui/icons-material';

interface ArrayItem {
  id: number;
  value: string;
}

interface ArrayFieldEditorProps {
  label: string;
  values: string[];
  onChange: (values: string[]) => void;
}

/**
 * Editable list of string values with add/remove controls.
 * Used for variant SKU fields like UPC, MPN, GTIN, etc.
 */
const ArrayFieldEditor = ({ label, values, onChange }: ArrayFieldEditorProps) => {
  const nextId = useRef(values.length);
  const [items, setItems] = useState<ArrayItem[]>(() =>
    values.map((value, i) => ({ id: i, value })),
  );

  const updateItems = useCallback((newItems: ArrayItem[]) => {
    setItems(newItems);
    onChange(newItems.map(item => item.value));
  }, [onChange]);

  const handleValueChange = (id: number, newValue: string) => {
    updateItems(items.map(item => item.id === id ? { ...item, value: newValue } : item));
  };

  const handleAdd = () => {
    updateItems([...items, { id: nextId.current++, value: '' }]);
  };

  const handleRemove = (id: number) => {
    updateItems(items.filter(item => item.id !== id));
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" gutterBottom>
        {label}
      </Typography>
      {items.map(item => (
        <Box
          key={item.id}
          sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
        >
          <TextField
            size="small"
            value={item.value}
            onChange={e => handleValueChange(item.id, e.target.value)}
            fullWidth
            placeholder={label}
          />
          <IconButton
            size="small"
            onClick={() => handleRemove(item.id)}
            color="error"
            title={`Remove ${label}`}
          >
            <RemoveIcon fontSize="small" />
          </IconButton>
        </Box>
      ))}
      <Button
        size="small"
        startIcon={<AddIcon />}
        onClick={handleAdd}
      >
        {'Add '}
        {label}
      </Button>
    </Box>
  );
};

export default ArrayFieldEditor;
